import { z } from "zod";

import {
  AccessLevelEnum,
  AccessStatusEnum,
  ConnectionTypeEnum,
  DestinationTypeEnum,
  NetworkTypeEnum,
  SubjectTypeEnum,
} from "@/constants/network";
import { extendDynamicSchema } from "@/utils/zod/extendSchema";

import type { PostNetworkRequest } from "../../-api";

const NetworkNameWhitespaceError = {
  code: z.ZodIssueCode.custom,
  params: { i18n: "custom.network.name_whitespace_only" },
};

const NetworkCategory = {
  SHARED_INTERNAL: "SHARED_INTERNAL",
  PERSONAL_USE: "PERSONAL_USE",
  COMPANY_ASSET: "COMPANY_ASSET",
  CUSTOMER_ENVIRONMENT: "CUSTOMER_ENVIRONMENT",
} as const;

export const NetworkType = NetworkTypeEnum;
export const ConnectionType = ConnectionTypeEnum;
export { NetworkCategory };
export const AccessLevel = AccessLevelEnum;
export const AccessStatus = AccessStatusEnum;
export const SubjectType = SubjectTypeEnum;

export const addNetworkSchema = z.object({
  // Step 1: ネットワーク基本情報
  networkName: z
    .string()
    .min(1)
    .max(100)
    .superRefine((val, ctx) => {
      if (val && val.trim().length === 0) {
        ctx.addIssue(NetworkNameWhitespaceError);
      }
    }),
  networkType: z.nativeEnum(NetworkType).optional(),
  connectionType: z.nativeEnum(ConnectionType).optional(),
  networkCategory: z.nativeEnum(NetworkCategory).optional(),
  purpose: z.string().max(200).optional(),
  destinationType: z.nativeEnum(DestinationTypeEnum).optional(),
  placeId: z.string().optional(),
  deviceId: z.string().optional(),
  adminContact: z.string().optional(),

  // Step 2: アクセス制御
  accessLevel: z.nativeEnum(AccessLevel).optional(),
  accessStatus: z.nativeEnum(AccessStatus).optional(),
  accessPeriodStartDate: z.string().optional(),
  accessPeriodEndDate: z.string().optional(),
  subjectType: z.nativeEnum(SubjectType).optional(),
});

export type AddNetworkFormData = z.infer<typeof addNetworkSchema>;

export const addNetworkDynamicSchema = extendDynamicSchema(addNetworkSchema, {
  ignoreCondition: (data: AddNetworkFormData, fieldName) =>
    (fieldName === "placeId" &&
      data?.destinationType !== DestinationTypeEnum.PLACE) ||
    (fieldName === "deviceId" &&
      data?.destinationType !== DestinationTypeEnum.DEVICE),
  requiredFields: ["networkName"],
});

export const convertToPostNetworkRequest = (
  data: AddNetworkFormData,
): PostNetworkRequest => {
  const request: PostNetworkRequest = {};

  // ネットワーク基本情報
  request.networkInformation = {
    networkName: data.networkName,
    networkType: data.networkType,
    connectionType: data.connectionType,
    networkCategory: data.networkCategory,
    purpose: data.purpose,
    destinationType: data.destinationType,
    placeId:
      data.destinationType === DestinationTypeEnum.PLACE
        ? data.placeId || null
        : null,
    deviceId:
      data.destinationType === DestinationTypeEnum.DEVICE
        ? data.deviceId || null
        : null,
  } as NonNullable<PostNetworkRequest["networkInformation"]>;

  // 管理責任者情報
  if (data.adminContact) {
    request.adminContact = {
      personId: data.adminContact,
    };
  }

  // アクセス制御
  if (
    data.accessLevel ||
    data.accessStatus ||
    data.subjectType ||
    data.accessPeriodStartDate ||
    data.accessPeriodEndDate
  ) {
    request.accessControl = {
      accessLevel: data.accessLevel,
      accessStatus: data.accessStatus,
      accessPeriodFrom: data.accessPeriodStartDate,
      accessPeriodTo: data.accessPeriodEndDate,
      subjectType: data.subjectType,
    };
  }

  return request;
};
